import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Info, ShieldOff, Phone, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const Disclaimer = () => {
  const lastUpdated = "January 10, 2026";

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="disclaimer-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <Card className="shadow-lg">
          <CardHeader className="border-b bg-amber-50">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-[#f39c12]" />
              <div>
                <CardTitle className="text-3xl font-bold text-[#2c3e50] font-['Manrope']">
                  Disclaimer
                </CardTitle>
                <p className="text-[#546e7a]">Last updated: {lastUpdated}</p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6 md:p-8 space-y-8">
            {/* Main Disclaimer Banner */}
            <section className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <ShieldOff className="w-10 h-10 text-[#e74c3c] flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-bold text-[#e74c3c] mb-2">
                    NOT A GOVERNMENT WEBSITE
                  </h2>
                  <p className="text-lg text-[#2c3e50] leading-relaxed">
                    Protect Elders from Fraud (PEFF) is a <strong>private initiative</strong> and is <strong>NOT</strong> an official government website or affiliated with the Indian Police, Cyber Cell, CBI, or any government agency. We are a private organization working to help senior citizens organize and track their fraud complaints.
                  </p>
                </div>
              </div>
            </section>

            {/* No Guarantee */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  No Guarantee of Recovery
                </h2>
              </div>
              <div className="bg-slate-50 rounded-lg p-6">
                <p className="text-base text-[#546e7a] leading-relaxed">
                  <strong className="text-[#2c3e50]">We do not guarantee money recovery.</strong> PEFF assists in organizing data and tracking the status of fraud complaints, but we have no control over investigation outcomes or fund recovery. The actual investigation and recovery process is handled by official law enforcement agencies.
                </p>
              </div>
            </section>

            {/* What We Do vs Don't Do */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                What We Do & Don't Do
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-[#27ae60] mb-3">What We Do</h3>
                  <ul className="space-y-2 text-[#2c3e50]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">✓</span>
                      Help organize your fraud complaint data
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">✓</span>
                      Provide a platform to track case status
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">✓</span>
                      Store and secure your documents
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#27ae60]">✓</span>
                      Provide helpful resources and guides
                    </li>
                  </ul>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-[#e74c3c] mb-3">What We Don't Do</h3>
                  <ul className="space-y-2 text-[#2c3e50]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#e74c3c]">✗</span>
                      File FIRs or police complaints on your behalf
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#e74c3c]">✗</span>
                      Guarantee recovery of lost money
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#e74c3c]">✗</span>
                      Conduct official investigations
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#e74c3c]">✗</span>
                      Provide legal advice or representation
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Official Channels */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Phone className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  Official Channels for Cyber Crime
                </h2>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <p className="text-base text-[#2c3e50] mb-4">
                  For official cyber crime complaints, please use these government resources:
                </p>
                <div className="space-y-3">
                  <a
                    href="https://cybercrime.gov.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                  >
                    <div>
                      <p className="font-bold text-[#2c3e50]">National Cyber Crime Portal</p>
                      <p className="text-sm text-[#546e7a]">cybercrime.gov.in</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-[#2c3e50]" />
                  </a>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <p className="font-bold text-[#2c3e50]">National Cyber Crime Helpline</p>
                    <a href="tel:1930" className="text-2xl font-bold text-[#e74c3c]">1930</a>
                    <p className="text-sm text-[#546e7a]">Available 24/7, Free to call</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Information Accuracy */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Information Accuracy
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                While we strive to provide accurate and up-to-date information, PEFF makes no warranties or representations as to the accuracy, completeness, or reliability of any information on this platform. Users should verify important information with official sources before taking action.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Third-Party Links
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                Our platform may contain links to external websites. PEFF is not responsible for the content, privacy practices, or availability of these third-party sites. Links do not imply endorsement of the linked website.
              </p>
            </section>

            {/* Legal Notice */}
            <section className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-[#f39c12] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-bold text-[#2c3e50] mb-2">Important Legal Notice</h3>
                  <p className="text-base text-[#2c3e50] leading-relaxed">
                    The information provided on PEFF is for general informational purposes only and should not be construed as legal advice. For legal matters, please consult with a qualified legal professional. By using this platform, you acknowledge and accept this disclaimer in full.
                  </p>
                </div>
              </div>
            </section>

            {/* Back to Home */}
            <div className="text-center pt-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#2c3e50] font-semibold hover:text-[#e74c3c] transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Disclaimer;
